import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Alert, Box, Snackbar, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ResizablePanel from "../../components/common/ResizablePanel";
import { useLayout } from "../../contexts/LayoutContext";
import {
  apiCreateCampaign,
  apiDeleteCampaign,
  apiGetCampaign,
  apiGetCampaignsLight,
  apiSendCampaign,
  apiStopCampaign,
  apiUpdateCampaign,
} from "../../graphQL/campaign";
import CampaignListPanel from "./panels/CampaignListPanel";
import CampaignDetailPanel from "./panels/CampaignDetailPanel";
import CampaignContactsPanel from "./panels/CampaignContactsPanel";
import CampaignAnalyticsPanel from "./panels/CampaignAnalyticsPanel";

const buildEmptyDraft = (domain) => ({
  id: `draft-${Date.now()}`,
  name: "New Campaign",
  domain: domain || "",
  status: "Draft",
  state: "draft",
  data: { contactFilter: { query: "", categories: [], status: "", cities: [], zipcodes: [] } },
  _isNew: true,
});

const normalizeCampaignForCompare = (campaign) => {
  if (!campaign || typeof campaign !== "object") return null;
  return {
    id: campaign.id || "",
    name: campaign.name || "",
    domain: campaign.domain || "",
    status: campaign.status || "Draft",
    data: campaign.data || {},
  };
};

const PageCampaigns = () => {
  const { isPanelOpen, togglePanel } = useLayout();
  const authorId = useSelector((state) => state.auth.userId);
  const userDomain = useSelector((state) => state.auth.user?.domain || "");
  const activeDomain = userDomain && userDomain !== "All" ? userDomain : "";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [dryRunning, setDryRunning] = useState(false);
  const [sending, setSending] = useState(false);
  // Bumped after a dry run so the contacts panel refetches lifecycle badges.
  const [lifecycleRefreshKey, setLifecycleRefreshKey] = useState(0);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiGetCampaignsLight({
        ...(activeDomain ? { domain: activeDomain } : {}),
      });
      setItems(Array.isArray(result?.campaigns) ? result.campaigns : []);
    } catch (err) {
      console.error("Failed to load campaigns:", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [activeDomain]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    setEditingCampaign(selectedCampaign ? { ...selectedCampaign } : null);
  }, [selectedCampaign]);

  // The list is loaded without `data`; hydrate the selected row on demand so
  // the detail / contacts / analytics panels get the full campaign.
  useEffect(() => {
    const id = selectedCampaign?.id;
    if (!id || selectedCampaign?._isNew || selectedCampaign?.data !== undefined) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const full = await apiGetCampaign(id);
        if (cancelled || !full?.id) return;
        setItems((prev) => prev.map((row) => (row?.id === full.id ? full : row)));
        setSelectedCampaign((prev) => (prev?.id === full.id ? full : prev));
      } catch (err) {
        console.error("Failed to load campaign:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCampaign?.id, selectedCampaign?._isNew, selectedCampaign?.data]);

  const campaigns = useMemo(() => {
    if (loading) return [];
    const base = Array.isArray(items) ? items : [];
    const withDraft =
      editingCampaign?._isNew &&
      !base.some((row) => row?.id === editingCampaign.id)
        ? [editingCampaign, ...base]
        : base;
    if (!searchFilter) return withDraft;
    const needle = String(searchFilter).trim().toLowerCase();
    return withDraft.filter((campaign) => {
      const name = String(campaign?.name || "").toLowerCase();
      const status = String(campaign?.status || "").toLowerCase();
      return name.includes(needle) || status.includes(needle);
    });
  }, [items, loading, searchFilter, editingCampaign]);

  const campaignDirty = useMemo(() => {
    if (!editingCampaign || !selectedCampaign) return false;
    const a = normalizeCampaignForCompare(selectedCampaign);
    const b = normalizeCampaignForCompare(editingCampaign);
    return JSON.stringify(a) !== JSON.stringify(b);
  }, [selectedCampaign, editingCampaign]);

  const handleSaveCampaign = useCallback(async (campaign) => {
    if (!campaign) return;
    if (["active", "processing"].includes(String(campaign.status || "").toLowerCase())) {
      console.error("Stop the campaign before saving changes.");
      return;
    }
    try {
      if (campaign._isNew) {
        const domain = campaign.domain || activeDomain || "";
        if (!domain) {
          console.error("Cannot create campaign: no domain selected. Pick a specific domain in the sidebar.");
          return;
        }
        const created = await apiCreateCampaign({
          name: campaign.name || "Untitled Campaign",
          domain,
          status: "Draft",
          data: campaign.data || {},
        });
        if (created?.id) {
          setItems((prev) => [created, ...prev.filter((row) => row?.id !== campaign.id)]);
          setSelectedCampaign(created);
          setEditingCampaign(created);
        }
        return;
      }
      if (!campaign.id) return;
      const updated = await apiUpdateCampaign({
        id: campaign.id,
        name: campaign.name || "Untitled Campaign",
        domain: campaign.domain || activeDomain || "",
        status: campaign.status || selectedCampaign?.status || "Draft",
        data: campaign.data || {},
      });
      if (updated?.id) {
        setItems((prev) => prev.map((row) => (row?.id === updated.id ? updated : row)));
        setSelectedCampaign(updated);
        setEditingCampaign(updated);
      }
    } catch (err) {
      console.error("Failed to save campaign:", err);
    }
  }, [selectedCampaign?.status, activeDomain]);

  const handleSetCampaignStatus = useCallback(async (campaign, nextStatus) => {
    if (!campaign?.id || campaign._isNew) return;
    try {
      const updated = await apiUpdateCampaign({
        id: campaign.id,
        name: campaign.name || "Untitled Campaign",
        status: nextStatus,
        data: campaign.data || {},
      });
      if (updated?.id) {
        setItems((prev) => prev.map((row) => (row?.id === updated.id ? updated : row)));
        if (selectedCampaign?.id === updated.id) {
          setSelectedCampaign(updated);
          setEditingCampaign(updated);
        }
      }
    } catch (err) {
      console.error(`Failed to set campaign status to ${nextStatus}:`, err);
    }
  }, [selectedCampaign?.id]);

  const handleActivateCampaign = useCallback(async (campaign) => {
    if (!campaign) return;
    const status = String(campaign.status || "").toLowerCase();
    if (status === "active" || status === "processing") return;
    await handleSetCampaignStatus(campaign, "Active");
  }, [handleSetCampaignStatus]);

  const handleStopCampaign = useCallback(async (campaign) => {
    if (!campaign?.id || campaign._isNew) return;
    const status = String(campaign.status || "").toLowerCase();
    if (!["active", "processing"].includes(status)) return;
    try {
      const stopped = await apiStopCampaign(campaign.id);
      if (stopped?.id) {
        setItems((prev) => prev.map((row) => (row?.id === stopped.id ? stopped : row)));
        if (selectedCampaign?.id === stopped.id) {
          setSelectedCampaign(stopped);
          setEditingCampaign(stopped);
        }
      }
    } catch (err) {
      console.error("Failed to stop campaign:", err);
    }
  }, [selectedCampaign?.id]);

  // Dry run: always dryRun:true, so the backend marks recipients but sends NO
  // email. After it completes we bump lifecycleRefreshKey so the contacts panel
  // refetches and the badges flip to sent (dry run).
  const handleDryRunCampaign = useCallback(async (campaign) => {
    if (!campaign?.id || campaign._isNew) return;
    setDryRunning(true);
    try {
      const result = await apiSendCampaign({ id: campaign.id, dryRun: true });
      const recipients = Number(result?.totalRecipients) || 0;
      const sent = Number(result?.sent) || 0;
      const failed = Number(result?.failed) || 0;
      const skipped = Number(result?.skipped) || 0;
      setLifecycleRefreshKey((k) => k + 1);
      setToast({
        open: true,
        severity: "success",
        message:
          `Dry run complete: ${sent} of ${recipients} recipients marked sent, 0 emails sent` +
          (failed || skipped ? ` (${failed} failed, ${skipped} skipped)` : ""),
      });
    } catch (err) {
      console.error("Failed to dry-run campaign:", err);
      setToast({ open: true, severity: "error", message: "Dry run failed. See console for details." });
    } finally {
      setDryRunning(false);
    }
  }, []);

  // Real send: dryRun:false. The backend still coerces to a dry run unless its
  // CAMPAIGN_EMAIL_SENDING_ENABLED kill-switch is on; surface that coercion.
  const handleSendCampaign = useCallback(async (campaign) => {
    if (!campaign?.id || campaign._isNew) return;
    setSending(true);
    try {
      const result = await apiSendCampaign({ id: campaign.id, dryRun: false });
      const recipients = Number(result?.totalRecipients) || 0;
      const sent = Number(result?.sent) || 0;
      const failed = Number(result?.failed) || 0;
      const skipped = Number(result?.skipped) || 0;
      setLifecycleRefreshKey((k) => k + 1);
      if (result?.dryRun) {
        setToast({
          open: true,
          severity: "warning",
          message: "Backend email sending is disabled; run was coerced to dry run",
        });
      } else {
        setToast({
          open: true,
          severity: "success",
          message:
            `Send complete: ${sent} of ${recipients} emails sent` +
            (failed || skipped ? ` (${failed} failed, ${skipped} skipped)` : ""),
        });
      }
    } catch (err) {
      console.error("Failed to send campaign:", err);
      setToast({ open: true, severity: "error", message: "Send failed. See console for details." });
    } finally {
      setSending(false);
    }
  }, []);

  // Confirmation lives in CampaignListPanel (same dialog pattern as Stop).
  const handleCopyCampaign = useCallback(async (campaign) => {
    if (!campaign?.id) return;
    // Clone content/audience into a real, saved Draft so it appears in the
    // list immediately and can be edited or deleted like any campaign.
    const data = campaign.data && typeof campaign.data === "object" ? campaign.data : {};
    const { suspension: _s, e2eV6Provenance: _p, ...cleanData } = data;
    const domain = campaign.domain || activeDomain || "";
    if (!domain) {
      console.error("Cannot copy campaign: no domain.");
      return;
    }
    try {
      const created = await apiCreateCampaign({
        name: `Copy of ${campaign.name || "Untitled Campaign"}`,
        domain,
        status: "Draft",
        data: cleanData,
      });
      if (created?.id) {
        setItems((prev) => [created, ...prev]);
        setSelectedCampaign(created);
        setEditingCampaign(created);
      }
    } catch (err) {
      console.error("Failed to copy campaign:", err);
    }
  }, [activeDomain]);

  const handleDeleteCampaign = useCallback(async (campaign) => {
    if (!campaign?.id || campaign._isNew) return;
    if (!["suspended", "draft", ""].includes(String(campaign?.status || "").toLowerCase())) return;
    try {
      await apiDeleteCampaign(campaign.id);
      setItems((prev) => prev.filter((row) => row.id !== campaign.id));
      setSelectedCampaign(null);
    } catch (err) {
      console.error("Failed to delete campaign:", err);
    }
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        overflow: "hidden",
        gap: isMobile ? 0.75 : 1,
      }}
    >
      {isPanelOpen("campaignList") && (
      <ResizablePanel
        edge="right"
        defaultWidth={isMobile ? window.innerWidth : 420}
        minWidth={isMobile ? window.innerWidth : 300}
        maxWidth={isMobile ? window.innerWidth : 620}
        fillRemaining={false}
        sx={isMobile ? { width: "100% !important" } : undefined}
      >
        <CampaignListPanel
          onClose={() => togglePanel("campaignList")}
          campaigns={campaigns}
          selectedId={selectedCampaign?.id}
          searchValue={searchInput}
          onSearchChange={(v) => {
            setSearchInput(v);
            if (!String(v || "").trim()) setSearchFilter("");
          }}
          onSearchSubmit={() => setSearchFilter(searchInput.trim())}
          onSelect={setSelectedCampaign}
          onActivate={handleActivateCampaign}
          onStop={handleStopCampaign}
          onSend={handleSendCampaign}
          onDelete={handleDeleteCampaign}
          onCopy={handleCopyCampaign}
          onAdd={() => setSelectedCampaign(buildEmptyDraft(activeDomain))}
        />
      </ResizablePanel>
      )}

      {isPanelOpen("campaignDetail") && (
      <ResizablePanel
        edge="right"
        defaultWidth={isMobile ? window.innerWidth : 580}
        minWidth={isMobile ? window.innerWidth : 360}
        maxWidth={isMobile ? window.innerWidth : 760}
        fillRemaining={false}
        sx={isMobile ? { width: "100% !important" } : undefined}
      >
        <CampaignDetailPanel
          onClose={() => togglePanel("campaignDetail")}
          campaign={editingCampaign}
          dirty={campaignDirty}
          dryRunning={dryRunning}
          onChange={setEditingCampaign}
          onSave={handleSaveCampaign}
          onActivate={handleActivateCampaign}
          onDryRun={handleDryRunCampaign}
          onSend={handleSendCampaign}
          sending={sending}
        />
      </ResizablePanel>
      )}

      {isPanelOpen("campaignContacts") && (
      <ResizablePanel
        edge="right"
        defaultWidth={isMobile ? window.innerWidth : 560}
        minWidth={isMobile ? window.innerWidth : 320}
        maxWidth={isMobile ? window.innerWidth : 900}
        fillRemaining
        sx={isMobile ? { width: "100% !important" } : undefined}
      >
        <CampaignContactsPanel
          onClose={() => togglePanel("campaignContacts")}
          campaign={editingCampaign}
          authorId={authorId}
          lifecycleRefreshKey={lifecycleRefreshKey}
        />
      </ResizablePanel>
      )}

      {isPanelOpen("campaignAnalytics") && (
      <ResizablePanel
        edge="right"
        defaultWidth={isMobile ? window.innerWidth : 520}
        minWidth={isMobile ? window.innerWidth : 320}
        maxWidth={isMobile ? window.innerWidth : 900}
        fillRemaining={!isPanelOpen("campaignContacts")}
        sx={isMobile ? { width: "100% !important" } : undefined}
      >
        <CampaignAnalyticsPanel
          onClose={() => togglePanel("campaignAnalytics")}
          campaign={editingCampaign}
          authorId={authorId}
          lifecycleRefreshKey={lifecycleRefreshKey}
        />
      </ResizablePanel>
      )}

      <Snackbar
        open={toast.open}
        autoHideDuration={9000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          sx={{ whiteSpace: "pre-line", maxWidth: 720, alignItems: "flex-start" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PageCampaigns;
